import React, { useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Loader2, ThumbsUp, Lightbulb } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface ContentResponseEditorProps {
  contentId: string;
  contentTitle: string;
  contentType: string;
  prompt?: string;
}

export const ContentResponseEditor: React.FC<ContentResponseEditorProps> = ({
  contentId,
  contentTitle,
  contentType,
  prompt
}) => {
  const { toast } = useToast();
  const [response, setResponse] = useState('');
  const [aiFeedback, setAiFeedback] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showEditor, setShowEditor] = useState(false);

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link'],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'link'
  ];

  const getPromptText = () => {
    if (prompt) return prompt;
    
    switch (contentType) {
      case 'rich_text':
      case 'text':
        return `Reflect on the content "${contentTitle}". Share your thoughts, insights, and how you might apply this in your practice.`;
      case 'video':
        return `After viewing "${contentTitle}", describe your key takeaways and how you plan to implement what you learned.`;
      case 'interactive':
        return `Complete the interactive activity and reflect on your experience with "${contentTitle}".`;
      default:
        return `Reflect on "${contentTitle}" and share your insights.`;
    }
  };

  const handleGetFeedback = async () => {
    if (!response.trim()) {
      toast({
        title: "Response Required",
        description: "Please write a response before requesting feedback.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('provide-content-feedback', {
        body: {
          response: response,
          contentTitle: contentTitle,
          contentType: contentType
        }
      });

      if (error) throw error;

      if (data?.feedback) {
        setAiFeedback(data.feedback);
        toast({
          title: "✨ Feedback Received",
          description: "AI has analyzed your response and provided personalized feedback."
        });
      }
    } catch (error) {
      console.error('Error getting feedback:', error);
      toast({
        title: "Error",
        description: "Failed to generate feedback. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      // Get current user ID
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      const userId = currentUser?.id || 'anonymous';
      
      // Save response to user's learning record
      const { error } = await supabase
        .from('course_content')
        .update({
          metadata: {
            user_responses: {
              [userId]: {
                response,
                feedback: aiFeedback,
                timestamp: new Date().toISOString()
              }
            }
          }
        })
        .eq('id', contentId);

      if (error) throw error;

      toast({
        title: "✓ Response Saved",
        description: "Your reflection has been saved successfully."
      });
    } catch (error) {
      console.error('Error saving response:', error);
      toast({
        title: "Error",
        description: "Failed to save your response.",
        variant: "destructive"
      });
    }
  };

  if (!showEditor) {
    return (
      <Button
        variant="outline"
        onClick={() => setShowEditor(true)}
        className="w-full mt-4"
      >
        <Sparkles className="h-4 w-4 mr-2" />
        Practice & Respond
      </Button>
    );
  }

  return (
    <Card className="mt-4 border-primary/20">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          Practice Exercise
        </CardTitle>
        <CardDescription>{getPromptText()}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-background rounded-lg border">
          <ReactQuill
            theme="snow"
            value={response}
            onChange={setResponse}
            modules={modules}
            formats={formats}
            placeholder="Share your thoughts, insights, and reflections here..."
            className="min-h-[200px]"
          />
        </div>

        <div className="flex gap-2">
          <Button
            onClick={handleGetFeedback}
            disabled={loading || !response.trim()}
            className="flex-1"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Get AI Feedback
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={handleSave}
            disabled={!response.trim()}
          >
            Save Response
          </Button>
        </div>

        {aiFeedback && (
          <Alert className="bg-gradient-to-br from-primary/5 to-purple-500/5 border-primary/20">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-1">
                <Badge variant="secondary" className="flex items-center gap-1">
                  <Lightbulb className="h-3 w-3" />
                  AI Coach
                </Badge>
              </div>
              <AlertDescription className="flex-1">
                <div 
                  className="prose prose-sm max-w-none dark:prose-invert"
                  dangerouslySetInnerHTML={{ __html: aiFeedback }}
                />
              </AlertDescription>
            </div>
          </Alert>
        )}

        {aiFeedback && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <ThumbsUp className="h-4 w-4" />
            <span>Use this feedback to deepen your understanding and improve your practice.</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
